import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, AlertTriangle } from 'lucide-react';
import { useAuth } from './AuthProvider';

const REQUIRED_ROLE = 'STATUS_PAGE_ADMIN';

export default function AuthGuard({ children }: { children: ReactNode }) {
  const { initialized, authenticated, hasRole, login, error, init } = useAuth();

  useEffect(() => { init(); }, [init]);

  if (!initialized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication Unavailable</h2>
          <p className="text-gray-600 mb-4">Unable to connect to the authentication service.</p>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Go to Status Page
          </Link>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    login();
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-600">Redirecting to login...</p>
      </div>
    );
  }

  if (!hasRole(REQUIRED_ROLE)) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-rose-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You do not have the required role to access the admin panel.
          </p>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Go to Status Page
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
